import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header.js";
import Confirmation from "../../common/components/Confirmation.js";
import Backdrop from "@mui/material/Backdrop";
import AlertMessage from "../../common/components/AlertMessage.js";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { BsQrCodeScan } from "react-icons/bs";
import { FaCheckCircle } from "react-icons/fa";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL.js";
import { getSessionContext } from "../../common/utils/sessionContext.js";
import { setManyToLocalStorage, setToLocalStorage } from "../../common/utils/localStorage.js";
import * as Sentry from "@sentry/react";
import "../../styles/qrCheckin.css";

const QRCheckIn = () => {
  const navigate = useNavigate();
  const [showTodoPopup, setShowTodoPopup] = useState(false);
  const [showResidentValue, setShowResidentValue] = useState(null);
  const [showResidentList, setShowResidentList] = useState([]);
  const [showResidentId, setShowResidentId] = useState(0);
  const [alertStatus, setAlertStatusBase] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAssociatedClientsForCaregiver();
  }, []);

  const getAssociatedClientsForCaregiver = async () => {
    setShowResidentList([]);
    setLoading(true);
    try {
      const { caregiverId, communityId } = getSessionContext();
      const responseData = await APIService.getData(URL.getAssociatedClientsByCaregiverId + "?caregiverId=" + caregiverId + "&communityId=" + communityId);
      if (responseData.code == 200) {
        const updatedResidentList = [];
        const residentIdList = [];
        (responseData.data || []).forEach((resident) => {
          updatedResidentList.push({
            label: resident.clientName,
            id: resident.clientId,
            unit: resident.unit,
          });
          residentIdList.push(resident.clientId);
        });
        setToLocalStorage("ResidentIdList", JSON.stringify(residentIdList));
        setShowResidentList(updatedResidentList);
      } else {
        setAlertStatusBase({
          message: responseData.message,
          status: responseData.code,
          origin: "center",
        });
      }
      setLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      setLoading(false);
      setAlertStatusBase({
        message: "Error while fetch associated clients for caregiver",
        status: "400",
        origin: "center",
      });
    }
  };

  const closeTodoConfirmation = () => setShowTodoPopup(false);

  const confirmationCallback = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { caregiverId } = getSessionContext();
    const data = {
      caregiverId,
      clientId: showResidentId,
    };
    try {
      const responseData = await APIService.postData(URL.caregiverCheckin, data);
      setLoading(false);
      if (responseData.code == 200) {
        const selectedResident = showResidentList.find((resident) => resident.id == showResidentId);
        setShowTodoPopup(false);
        setManyToLocalStorage({
          ResidentName: showResidentValue,
          ResidentId: showResidentId,
          roomNumber: selectedResident?.unit || "",
          CaregiverCheckinId: responseData.data.caregiverCheckinDetailsId,
          CheckinTime: responseData?.data?.checkinTime || new Date().toISOString(),
        });
        navigate("/newResident");
      } else {
        setAlertStatusBase({
          message: responseData.message,
          status: responseData.code,
          origin: "center",
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      setLoading(false);
      setAlertStatusBase({
        message: "Error while checkin resident",
        status: "400",
        origin: "center",
      });
    }
  };

  const handleQRScanner = async () => {
    if (typeof window.flutter_inappwebview === "undefined") {
      setAlertStatusBase({
        message: "QR scanning is only supported inside the app. Please open this in the mobile application.",
        status: "400",
        origin: "center",
      });
      return;
    }
    try {
      const response = await window.flutter_inappwebview.callHandler("scanQR");
      const { status, data } = response;
      if (!status) return;
      const resident = showResidentList.find((item) => item.unit == data);
      if (!resident) {
        setAlertStatusBase({
          message: "This client isn't assigned to you. Please Contact Administrator.",
          status: "403",
          origin: "center",
        });
        return;
      }
      setShowResidentValue(resident.label);
      setShowResidentId(resident.id);
      setShowTodoPopup(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <>
      <Header />
      <div className="home-container">
        <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
          <img src={require("../../assets/btTree.png")} className="loading-img" alt="Loading" />
        </Backdrop>
        {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={() => setAlertStatusBase("")} /> : null}

        <div className="home-inner-container newcheckin-page">
          <div className="heading vitals-heading">
            <IoChevronBackCircleSharp className="vitals-preview-header" onClick={() => navigate("/home")} />
            <span style={{ fontWeight: "bold" }}>Check-In</span>
            <span />
          </div>

          <div className="newcheckin-card">
            <p className="newcheckin-subtext">Tap below to open the scanner and scan the resident's QR code.</p>
            <button type="button" className="newcheckin-qr-frame" onClick={handleQRScanner} aria-label="Open scanner">
              <BsQrCodeScan />
            </button>
            <div className="newcheckin-divider">OR</div>
            <div className="newcheckin-manual-cta">
              <button type="button" className="home-menus newresident-checkout bg-visit-gradient newcheckin-manual-btn" onClick={() => navigate("/checkin")}>
                <FaCheckCircle className="caregiver-checkin-icon" />
                <span>Manual Checkin</span>
              </button>
            </div>
          </div>
        </div>
        <Confirmation username={showResidentValue} content="Are you sure you want to check-in?" open={showTodoPopup} closeConfirmation={closeTodoConfirmation} processCallback={confirmationCallback} />
      </div>
    </>
  );
};

export default QRCheckIn;
